import React, { useEffect, useRef, useState } from "react";

/**
 * SoulOrb — a quiet, breathing orb.
 *
 * Purely visual. A sensory anchor: glow (nourishment), pulse (alignment),
 * color shift (emotional state), expansion (confidence). No audio — the
 * orb's original 432Hz tone was removed as part of the app-wide UI-sound
 * cleanup. The `toneOnMount`, `toneSeed`, and `playToneOnPulse` props
 * are retained as inert placeholders so historical call sites don't break.
 *
 * PROPS
 *   size           "sm" | "md" | "lg"  — visual size
 *   mode           "default" | "aligned" | "confident" | "grounded"
 *                  → shifts the palette. Aligned = warm white. Confident
 *                    = gold. Grounded = deep terracotta.
 *   pulseSeed      any changing primitive; a new value fires a one-shot
 *                  pulse animation.
 *   expandSeed     any changing primitive; a new value fires a slow
 *                  expansion (confidence gesture).
 *   ariaLabel      screen reader description
 */

const SIZE_PX = { sm: 44, md: 84, lg: 220 };

const PALETTES = {
  default:   { core: "hsl(24, 55%, 68%)",  halo: "hsl(24, 55%, 68%)",  edge: "hsl(20, 40%, 45%)" },
  aligned:   { core: "hsl(36, 60%, 92%)",  halo: "hsl(36, 55%, 80%)",  edge: "hsl(24, 45%, 55%)" },
  confident: { core: "hsl(40, 78%, 70%)",  halo: "hsl(38, 78%, 60%)",  edge: "hsl(30, 55%, 42%)" },
  grounded:  { core: "hsl(14, 55%, 58%)",  halo: "hsl(14, 50%, 45%)",  edge: "hsl(10, 45%, 30%)" },
};

export default function SoulOrb({
  size = "md",
  mode = "default",
  glow = 0.5,             // 0..1 — nourishment brightness (halo + bloom + drop-shadow)
  pulseSeed,
  expandSeed,
  ariaLabel = "Soul orb",
  className = "",
  testid = "soul-orb",
}) {
  const px = SIZE_PX[size] || SIZE_PX.md;
  const palette = PALETTES[mode] || PALETTES.default;
  const g = Math.max(0, Math.min(1, glow));       // clamp
  const [pulseKey, setPulseKey] = useState(0);
  const [expandKey, setExpandKey] = useState(0);
  const firstMount = useRef(true);

  // React to pulseSeed / expandSeed changes — visual only, no audio.
  useEffect(() => {
    if (firstMount.current) return; // ignore initial render
    setPulseKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseSeed]);

  useEffect(() => {
    if (firstMount.current) return;
    setExpandKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandSeed]);

  useEffect(() => { firstMount.current = false; }, []);

  // Derived brightness values. Halo alpha, bloom alpha, and drop-shadow all
  // scale together so the orb visibly "glows" more as the soul is nourished.
  const haloAlphaHex = Math.round((0.18 + g * 0.42) * 255).toString(16).padStart(2, "0");
  const bloomAlphaHex = Math.round((0.10 + g * 0.35) * 255).toString(16).padStart(2, "0");
  const shadowPx = 6 + Math.round(g * 34);
  const shadowAlphaHex = Math.round((0.10 + g * 0.35) * 255).toString(16).padStart(2, "0");

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-testid={testid}
      data-mode={mode}
      data-glow={Math.round(g * 100)}
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: px, height: px }}
    >
      {/* Outer bloom — brightens with glow, always on when g > 0.15. */}
      {g > 0.15 && (
        <span
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: `${-Math.round(g * 22)}px`,
            background: `radial-gradient(circle, ${palette.halo}${bloomAlphaHex} 0%, ${palette.halo}00 65%)`,
            animation: "orb-breath 6s ease-in-out infinite",
          }}
        />
      )}

      {/* Outer halo — always present; opacity scales with glow. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle, ${palette.halo}${haloAlphaHex} 0%, ${palette.halo}00 68%)`,
          animation: "orb-breath 6s ease-in-out infinite",
        }}
      />

      {/* One-shot pulse ring — remounted on each pulseKey change. */}
      {pulseKey > 0 && (
        <span
          key={`pulse-${pulseKey}`}
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            border: `1px solid ${palette.halo}`,
            animation: "orb-pulse 1.6s ease-out forwards",
          }}
        />
      )}

      {/* Confidence expansion — larger, slower glow ring. */}
      {expandKey > 0 && (
        <span
          key={`expand-${expandKey}`}
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, ${palette.halo}55 0%, transparent 70%)`,
            animation: "orb-expand 2.4s ease-out forwards",
          }}
        />
      )}

      {/* The core orb — now renders the SIMPLIFIED N8ONE emblem mark so
          every inline SoulOrb carries the same brand identity. The halo
          + bloom + pulse ring behavior above is preserved exactly, so
          every existing call-site keeps its "pulses on selection" and
          "glows with nourishment" behaviour unchanged. */}
      <img
        src="/brand/n8-emblem-sm.png"
        alt=""
        aria-hidden
        draggable="false"
        className="relative block select-none"
        style={{
          width: "82%",
          height: "82%",
          objectFit: "contain",
          filter: `drop-shadow(0 0 ${shadowPx}px ${palette.halo}${shadowAlphaHex}) drop-shadow(0 2px 6px rgba(140,106,93,0.2))`,
        }}
      />
    </div>
  );
}
